import { useServices, useStore } from "@hq/client-react";
import type { Area, Project } from "@hq/contracts";
import { groupProjectsByArea, orderChanges } from "@hq/core";
import { DragDropRoot, moveItems, restoreDragFocus, useDroppable, useSortable } from "@z/dnd";
import { cn, toast } from "@z/ui";
import { GripVertical } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

type AreaItems = Record<string, Project[]>;
const areaKey = (area: string) => `project-area:${area}`;

/** One context lets rows reorder locally and move into another area's drop target. */
export function ProjectOrder({
  projects,
  areas = [],
  header,
  children,
  renderArea = (_area, content) => content,
}: {
  projects: readonly Project[];
  areas?: readonly Area[];
  header?: ReactNode;
  children: (project: Project) => ReactNode;
  renderArea?: (area: string, content: ReactNode) => ReactNode;
}) {
  const services = useServices();
  const store = useStore();
  const groups = groupProjectsByArea(projects, areas);
  if (!groups.some(([area]) => !area)) groups.unshift(["", []]);
  const items: AreaItems = Object.fromEntries(groups.map(([area, rows]) => [areaKey(area), rows]));
  const [preview, setPreview] = useState<AreaItems | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [overArea, setOverArea] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const focusAfterDrop = useRef<string | null>(null);
  useLayoutEffect(() => {
    // Cross-area drops remount the handle. Focus only after that DOM commit.
    if (draggedId !== null || saving || focusAfterDrop.current === null) return;
    restoreDragFocus(rootRef.current, focusAfterDrop.current);
    focusAfterDrop.current = null;
  }, [draggedId, saving]);
  const dragged = projects.find((project) => project.id === draggedId);
  return (
    <DragDropRoot
      onBeforeDragStart={(event) => {
        if (saving) event.preventDefault();
      }}
      onDragStart={({ operation }) => {
        setDraggedId(String(operation.source?.id));
        setPreview(items);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        const source = projects.find((project) => project.id === event.operation.source?.id);
        const area = event.operation.target?.data["area"];
        setOverArea(typeof area === "string" ? area : null);
        if (!source || typeof area !== "string" || area !== (source.area ?? "")) {
          // Keep the source mounted while hovering another area, including a collapsed one.
          setPreview(items);
          return;
        }
        const key = areaKey(area);
        setPreview((current) => ({
          ...items,
          [key]: moveItems(current?.[key] ?? items[key] ?? [], event),
        }));
      }}
      onDragEnd={async (event) => {
        const id = String(event.operation.source?.id);
        const keyboard = event.nativeEvent instanceof KeyboardEvent;
        try {
          const source = projects.find((project) => project.id === id);
          const target = event.operation.target;
          const area = target?.data["area"];
          if (event.canceled || !source || !target || typeof area !== "string") return;
          const key = areaKey(area);
          const movedArea = area !== (source.area ?? "");
          const ordered = [...(preview?.[key] ?? items[key] ?? [])];
          if (movedArea) {
            const index = ordered.findIndex((project) => project.id === target.id);
            ordered.splice(index < 0 ? ordered.length : index, 0, source);
          } else if (ordered.every((project, index) => project.id === items[key]?.[index]?.id)) {
            return;
          }
          setSaving(true);
          if (movedArea) await services.projects.setArea(id, area || null);
          for (const change of orderChanges(ordered))
            await services.projects.reorder(change.id, change.sortOrder);
          if (area) await store.kv.set(`sidebar.expanded.area.${area}`, true);
          setAnnouncement(`Moved ${source.name}${area ? ` to ${area}` : " out of its area"}.`);
        } catch {
          toast({
            title: "Could not move project",
            description: "Please try again.",
            variant: "danger",
          });
        } finally {
          if (keyboard) focusAfterDrop.current = id;
          setSaving(false);
          setPreview(null);
          setDraggedId(null);
          setOverArea(null);
        }
      }}
    >
      <div ref={rootRef} tabIndex={-1} className="flex flex-col gap-2">
        <div role="status" className="sr-only">
          {announcement}
        </div>
        {groups.map(([area]) => (
          <ProjectDropArea
            key={area}
            area={area}
            enabled={!!dragged && (dragged.area ?? "") !== area && !saving}
            active={overArea === area && (dragged?.area ?? "") !== area}
          >
            {!area && header}
            {renderArea(
              area,
              (preview?.[areaKey(area)] ?? items[areaKey(area)] ?? []).map((project, index) => (
                <SortableProject key={project.id} project={project} index={index} disabled={saving}>
                  {children(project)}
                </SortableProject>
              )),
            )}
          </ProjectDropArea>
        ))}
      </div>
    </DragDropRoot>
  );
}

function ProjectDropArea({
  area,
  enabled,
  active,
  children,
}: {
  area: string;
  enabled: boolean;
  active: boolean;
  children: ReactNode;
}) {
  const { ref } = useDroppable({
    id: areaKey(area),
    accept: "sidebar-project",
    disabled: !enabled,
    collisionPriority: -1,
    data: { area, label: area || "Standalone projects" },
  });
  return (
    <div
      ref={ref}
      data-project-area={area}
      className={cn(
        "rounded-md transition-colors empty:hidden",
        enabled && "empty:block empty:min-h-8",
        active && "bg-accent-muted/40 ring-1 ring-inset ring-accent/50",
      )}
    >
      {children}
    </div>
  );
}

function SortableProject({
  project,
  index,
  disabled,
  children,
}: {
  project: Project;
  index: number;
  disabled: boolean;
  children: ReactNode;
}) {
  const { ref, handleRef, isDragSource } = useSortable({
    id: project.id,
    index,
    group: areaKey(project.area ?? ""),
    type: "sidebar-project",
    accept: "sidebar-project",
    disabled,
    data: { label: project.name, area: project.area ?? "" },
  });
  return (
    <div
      ref={ref}
      data-project-id={project.id}
      className={cn(
        "group/project relative",
        isDragSource && "z-20 rounded-md bg-surface-raised shadow-lg",
      )}
    >
      {children}
      <button
        ref={handleRef}
        type="button"
        aria-label={`Reorder ${project.name}`}
        title="Drag to reorder or move to an area · Space, arrow keys, Space"
        data-drag-handle={project.id}
        className="absolute right-3 top-1/2 flex size-6 -translate-y-1/2 touch-none cursor-grab items-center justify-center rounded-sm bg-surface text-fg-muted opacity-0 hover:text-fg focus:opacity-100 group-hover/project:opacity-100 focus-ring"
      >
        <GripVertical className="size-3.5" />
      </button>
    </div>
  );
}
